import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faLocationDot,
  faGlobe,
  faPhone,
  faEnvelope,
  faClock,
  faEye,
  faUser,
  faBolt,
  faCalendarDays,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import useFetch from "../../hooks/useFetch";

const filterOptions = [
  { value: "area", label: "🏘️ Area" },
  { value: "city", label: "🏙️ City" },
  { value: "district", label: "🌐 District" },
  { value: "state", label: "🌍 State" },
  { value: "pin", label: "📍 PIN" },
];

const VocalForLocalHome = () => {
  const { slug } = useParams();
  const isFoundHer = slug === "foundher";
  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();

  const userId = authState?.user?._id;
  const token = authState?.token;

  const [filterType, setFilterType] = useState("city");
  const [filterValue, setFilterValue] = useState("");
  const [listings, setListings] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberedFilters, setRememberedFilters] = useState([]);
  const [rememberLoading, setRememberLoading] = useState(false);

  useEffect(() => {
    setFilterValue(
      filterType === "pin"
        ? authState?.user?.pinCode || ""
        : filterType === "city"
          ? authState?.user?.city || ""
          : filterType === "state"
            ? authState?.user?.state || ""
            : authState?.user?.area || "",
    );
  }, [filterType, authState?.user]);

  // ---------------- FETCH REMEMBERED FILTERS ----------------
  const fetchRememberedFilters = async () => {
    const res = await fetchData({
      url: "/pd/finditlocal/remembered/list",
      method: "post",
      data: { userId },
      headers: {
        Authorization: token,
      },
    });

    if (res?.status) {
      setRememberedFilters(res.records || []);
    }
  };

  useEffect(() => {
    if (token) fetchRememberedFilters();
  }, [token]);

  // ---------------- GET IT ----------------
  // ---------------- GET IT ----------------
  const handleGetIt = async () => {
    if (!filterValue.trim()) {
      setError("Please enter a value for the selected filter");
      return;
    }

    setLoading(true);
    setError("");
    setSelectedBusiness(null);

    try {
      const res = await fetchData({
        url: "/pd/finditlocal/search",
        method: "post",
        data: {
          filterType, // "state"
          filterValue: filterValue.trim(), // "Maharashtra"
        },
        headers: {
          Authorization: token,
        },
      });

      // Some useFetch implementations return the whole axios/fetch response
      // Others return only the body. Handle both.
      const body = res?.data ?? res;

      if (body?.status) {
        const records = body.records || [];
        setListings(records);
        if (records.length === 0) {
          setError(
            "No local businesses found for this filter. Try something else! ✨",
          );
        }
      } else {
        // Backend returned 400 / validation error
        const msg =
          body?.message ||
          body?.error ||
          body?.msg ||
          "Oops! Something went wrong. Please try again 🔥";
        setError(msg);
        setListings([]);
        console.error("Search API error body:", body); // ← look here in console
      }
    } catch (err) {
      // This catches real network errors + the "Unknown promise rejection"
      console.error("Search request failed:", err);

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Network / server error. Please try again.";
      setError(msg);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- REMEMBER IT ----------------
  const handleRememberIt = async () => {
    if (!filterValue.trim()) {
      setError("Enter a value to remember first!");
      return;
    }

    setRememberLoading(true);
    setError("");

    try {
      const res = await fetchData({
        url: "/pd/finditlocal/remember",
        method: "post",
        data: {
          userId,
          filterType,
          filterValue: filterValue.trim(),
        },
        headers: {
          Authorization: token,
        },
      });

      const body = res?.data ?? res;

      if (body?.status) {
        const newFilter = body.record || {
          id: Date.now(),
          type: filterType,
          value: filterValue.trim(),
        };

        setRememberedFilters((prev) => [newFilter, ...prev].slice(0, 5));
        setRememberSuccess(true);
        setTimeout(() => setRememberSuccess(false), 2000);
      } else {
        setError(body?.message || body?.error || "Could not save this filter.");
      }
    } catch (err) {
      console.error("Remember request failed:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not save this filter. Please try again.",
      );
    } finally {
      setRememberLoading(false);
    }
  };

  const handleBusinessClick = (business) => {
    setSelectedBusiness(business);
    setTimeout(() => {
      document.getElementById("business-details")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const closeDetails = () => setSelectedBusiness(null);

  // ---------------- CONTACT HELPERS ----------------
  const isCallAllowed = (item) => {
    if (!item.callDays && !item.callTiming) return true;

    const now = new Date();
    const currentDay = now
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();

    const days =
      item.callDays
        ?.toLowerCase()
        .split(",")
        .map((d) => d.trim()) || [];

    if (days.length && !days.includes(currentDay)) return false;

    if (item.callTiming?.includes("-")) {
      const [start, end] = item.callTiming.split("-");
      const toHours = (t) => {
        const [h, m] = t.trim().split(":").map(Number);
        return h + (m || 0) / 60;
      };
      const current = now.getHours() + now.getMinutes() / 60;
      return current >= toHours(start) && current <= toHours(end);
    }

    return true;
  };

  const handleCall = (item) => {
    const number = item?.mobileNumber;
    if (!number) {
      alert("📵 No phone number available");
      return;
    }
    if (!isCallAllowed(item)) {
      alert("📵 Calls allowed only during specified days/timing");
      return;
    }
    window.location.href = `tel:${number}`;
  };

  const handleWhatsApp = (item) => {
    const number = item.whatsappNumber || item.mobileNumber;
    if (!number) {
      alert("💬 No WhatsApp number available");
      return;
    }
    const cleanNumber = String(number).replace(/[^\d]/g, "");
    const message = encodeURIComponent(
      `Hi ${item.contactPerson || item.postedByName || "there"},\n\n` +
        `I saw your listing on Vocal for Local.\n\n` +
        `👉 Title: ${item.businessName || item.title}\n` +
        `📍 Location: ${item.area || "Not specified"}, ${item.city || ""}\n\n` +
        `I am interested. Please contact..\n\nThanks!`,
    );
    window.open(`https://wa.me/${cleanNumber}?text=${message}`, "_blank");
  };

  const handleEmail = (item) => {
    const email = item.emailId;
    if (!email) {
      alert("✉ No email available");
      return;
    }
    window.location.href = `mailto:${email}`;
  };

  const handleMicroSite = (business) => {
    handleBusinessClick(business);
  };

  const handleStreakRedemption = (business) => {
    window.location.href = `/khareedo?shopId=${business._id || business.id || ""}`;
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white font-sans">
      <div className="bg-zinc-900 border-b border-zinc-800 px-4 py-2">
        <div
          className={`w-full flex items-center justify-between rounded-2xl p-4 transition-all ${
            isFoundHer
              ? "bg-gradient-to-r from-pink-600 via-fuchsia-600 to-rose-600"
              : ""
          }`}
        >
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center text-2xl mt-1 ${
                isFoundHer
                  ? "bg-gradient-to-br from-pink-200 to-rose-100"
                  : "bg-gradient-to-br from-cyan-400 to-yellow-400"
              }`}
            >
              {isFoundHer ? "👩" : "🗣️"}
            </div>

            <div>
              <h1
                className={`flex justify-start text-xl lg:text-2xl font-bold tracking-tighter ${
                  isFoundHer ? "text-pink-50" : "text-yellow-200"
                }`}
              >
                {isFoundHer ? "FoundHer Local Marketplace" : "LocalFirst"}
              </h1>

              <p
                className={`text-xs mt-1 ${
                  isFoundHer ? "text-pink-100" : "text-gray-200"
                }`}
              >
                {isFoundHer
                  ? "Discover, support & shop from women entrepreneurs across India. 💖"
                  : "India Vocal for Local. Shop real. Support local. ✨"}
              </p>
            </div>
          </div>

          {rememberedFilters.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <span className={isFoundHer ? "text-pink-100" : "text-gray-200"}>
                Recent:
              </span>

              {rememberedFilters.slice(0, 3).map((f) => (
                <button
                  key={f.id}
                  onClick={() => {
                    setFilterType(f.type);
                    setFilterValue(f.value);
                  }}
                  disabled
                  className={`px-3 py-1 rounded-full transition-all flex items-center gap-1 ${
                    isFoundHer
                      ? "bg-pink-100/20 hover:bg-pink-100/30 text-pink-50 border border-pink-300/30"
                      : "bg-zinc-800 hover:bg-zinc-700 text-cyan-200"
                  }`}
                >
                  {f.type}: {f.value}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="w-full px-4 py-2">
        <div className="bg-zinc-900 rounded-2xl px-2 py-1 shadow-2xl border border-zinc-800 mb-2">
          <div className="flex flex-col lg:flex-row gap-2">
            <div className="flex-1">
              <label className="text-sm lg:text-sm font-medium text-gray-200 mb-1 flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="text-cyan-400"
                />{" "}
                Filter Type
              </label>
              <div className="grid grid-cols-3 lg:grid-cols-5 gap-0.5">
                {filterOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFilterType(option.value)}
                    className={`px-2 py-1 rounded-xl text-left transition-all border ${
                      filterType === option.value
                        ? "bg-zinc-800 border-cyan-400 shadow-lg shadow-cyan-500/30"
                        : "bg-zinc-950 border-zinc-700 hover:border-zinc-600"
                    }`}
                  >
                    <span className="leading-none text-xs lg:text-xs mb-1">
                      {option.label.split(" ")[0]}{" "}
                      {option.label.split(" ").slice(1).join(" ")}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1">
              <label className="text-sm lg:text-sm font-medium text-gray-200 mb-1 flex items-center gap-2">
                Enter {filterType}
              </label>
              <div className="flex flex-col lg:flex-row gap-0.5">
                <div className="flex flex-row">
                  <input
                    type="text"
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleGetIt()}
                    placeholder={`e.g. ${
                      filterType === "pin"
                        ? "421204"
                        : filterType === "city"
                          ? "Mumbai"
                          : filterType === "state"
                            ? "Maharashtra"
                            : "Palava"
                    }`}
                    style={{ width: "250px" }}
                    className="bg-zinc-950 border border-zinc-700 focus:border-cyan-400 rounded-xl px-2 py-0.5 text-xs lg:text-sm placeholder:text-gray-500 focus:outline-none"
                  />
                  <button
                    onClick={handleGetIt}
                    disabled={loading}
                    className="w-fit px-2 py-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 rounded-xl text-xs lg:text-sm flex items-center gap-2 shadow-lg shadow-cyan-500/30 active:scale-95 disabled:opacity-70"
                  >
                    {loading ? "Finding..." : "Get It"}{" "}
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  </button>
                </div>
                <div className="flex-1 w-full">
                  <button
                    id="rememberBtn"
                    onClick={handleRememberIt}
                    disabled
                    className="w-fit px-2 py-1 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 rounded-xl text-xs lg:text-sm flex items-center gap-2 transition-all active:scale-95"
                  >
                    Remember It 💾
                  </button>
                </div>
              </div>
            </div>
          </div>
          {error && (
            <div className="mt-2 px-2 py-1 bg-red-950/60 border border-red-900 rounded-xl text-red-400">
              {error}
            </div>
          )}
        </div>

        {/* Listings Grid */}
        <div>
          <div className="flex items-end justify-between mb-2 border-b border-cyan-200 rounded-2xl py-2 px-2">
            <h2
              className={`px-4 text-lg lg:text-xl font-bold tracking-tighter ${
                isFoundHer ? "text-pink-400" : "text-cyan-200"
              }`}
            >
              Local {isFoundHer ? "Women" : ""} Gems
              <span className="text-lg lg:text-xl">🔥</span>
            </h2>
            {listings.length > 0 && (
              <p className="text-gray-200">{listings.length} businesses</p>
            )}
          </div>

          {listings.length === 0 && !loading ? (
            <div className="text-center py-1">
              <div className="mx-auto w-20 h-20 bg-zinc-900 rounded-full flex items-center justify-center text-xl lg:text-2xl mb-2">
                🏪
              </div>
              <h3 className="text-lg lg:text-xl text-gray-200">
                Ready to discover your neighbourhood{" "}
                {isFoundHer ? "Women Entreprenure" : ""}?
              </h3>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {listings.map((business, i) => (
                <div
                  key={business._id || i}
                  onClick={() => handleBusinessClick(business)}
                  className="group bg-zinc-900 border border-zinc-800 hover:border-cyan-400 rounded-2xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
                >
                  <div className="h-20 bg-zinc-800 relative">
                    {business.image ? (
                      <img
                        src={business.image}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-10 flex items-center justify-center text-lg lg:text-xl opacity-40">
                        🛍️ Local
                      </div>
                    )}
                    {/* <div className="absolute top-4 right-4 bg-black/70 px-3 py-1 rounded-full text-xs">
                      Local
                    </div> */}
                  </div>

                  <div className="px-2 py-1">
                    <h2 className="text-lg lg:text-xl text-cyan-200 font-semibold mb-2 line-clamp-2">
                      {business.businessName || business.title}
                    </h2>
                    <p className="text-gray-100 text-sm line-clamp-3 mb-4">
                      {business.description || business.briefDesc}
                    </p>

                    <div className="flex items-center gap-2 text-xs text-gray-300 mb-2">
                      <FontAwesomeIcon icon={faLocationDot} />{" "}
                      {business.city || business.location || filterValue}
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMicroSite(business);
                        }}
                        className="flex-1 w-fit px-2 py-1 bg-blue-600 text-white hover:bg-zinc-700 rounded-xl text-xs lg:text-sm flex items-center justify-center gap-2 transition-all"
                      >
                        <FontAwesomeIcon icon={faGlobe} /> Details
                      </button>
                      {/* <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStreakRedemption(business);
                        }}
                        disabled
                        className="flex-1 w-fit px-2 py-1 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-semibold rounded-xl text-xs lg:text-sm flex items-center justify-center gap-2 transition-all"
                      >
                        Streak 🔥
                      </button> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Business Detail View */}
        {selectedBusiness && (
          <div
            id="business-details"
            className="mt-2 bg-zinc-900 border border-zinc-700 rounded-2xl px-4 py-2"
          >
            <button
              onClick={closeDetails}
              className="float-right w-fit px-1 py-1 text-xl lg:text-2xl text-gray-200 hover:text-white"
            >
              ×
            </button>

            <h1 className="text-lg lg:text-xl font-bold text-yellow-200 mb-1">
              {selectedBusiness.businessName || selectedBusiness.title}
            </h1>
            <p className="text-cyan-300 text-sm lg:text-lg mb-2">
              {selectedBusiness.area}, {selectedBusiness.city}
            </p>
            <p className="text-gray-100 text-sm lg:text-lg mb-2">
              {selectedBusiness.description || selectedBusiness.briefDesc}
            </p>

            {/* TAGS */}
            {selectedBusiness.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-1">
                {selectedBusiness.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-zinc-800 px-3 py-1 rounded-full text-xs text-cyan-200"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {selectedBusiness.streakOffer && (
              <div className="text-sm text-purple-300 flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faBolt} /> {selectedBusiness.streakOffer}
              </div>
            )}

            {selectedBusiness.vibe && (
              <div className="text-sm text-cyan-300 italic mb-1">
                {selectedBusiness.vibe}
              </div>
            )}

            {/* CONTACT ACTIONS */}
            <div className="flex flex-row gap-2 mb-1">
              <button
                onClick={() => handleCall(selectedBusiness)}
                disabled={!isCallAllowed(selectedBusiness)}
                className={`text-xs w-fit px-2 py-1 rounded-md font-bold flex items-center gap-2 transition-all
                                    ${
                                      isCallAllowed(selectedBusiness)
                                        ? "bg-green-500 text-black"
                                        : "bg-gray-600 text-gray-50 cursor-not-allowed"
                                    }`}
              >
                <FontAwesomeIcon icon={faPhone} /> Call
              </button>

              <button
                onClick={() => handleWhatsApp(selectedBusiness)}
                className="text-xs w-fit px-2 py-1 rounded-md bg-green-600 text-white font-bold flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp
              </button>

              <button
                onClick={() => handleEmail(selectedBusiness)}
                className="text-xs w-fit px-2 py-1 rounded-md bg-blue-600 text-white font-bold flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faEnvelope} /> Email
              </button>
            </div>

            {/* AVAILABILITY / POSTED BY / EXPIRY / VIEWS */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-[11px] text-gray-100 mb-1">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faClock} className="text-cyan-400" />
                {selectedBusiness.callDays || "All Days"} |{" "}
                {selectedBusiness.callTiming || "24x7"}
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faUser} className="text-cyan-400" />
                {selectedBusiness.postedByName}
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faCalendarDays}
                  className="text-yellow-300"
                />
                {selectedBusiness.dateExpires &&
                  new Date(selectedBusiness.dateExpires).toLocaleDateString(
                    "en-IN",
                  )}
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faEye} className="text-cyan-400" />
                {selectedBusiness.viewCount || 0} Views
              </div>
            </div>

            <div className="flex gap-4 mt-2">
              {/* <button
                onClick={() => handleMicroSite(selectedBusiness)}
                className="flex-1 w-fit px-2 py-1 bg-zinc-800 rounded-xl text-sm lg:text-lg"
              >
                <FontAwesomeIcon icon={faGlobe} /> Details
              </button> */}
              <button
                onClick={() => handleStreakRedemption(selectedBusiness)}
                disabled
                className="w-fit px-2 py-1 bg-gradient-to-r bg-blue-600  text-white font-bold rounded-xl text-xs lg:text-sm"
              >
                🔥 Streak Redemption / Order Now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VocalForLocalHome;
